// Client-side spiegel van de beslisboom voor de overzichtsweergave
// (stap 4). De server herrekent alles bij het indienen — dit bestaat
// alleen zodat de klant vooraf het juiste kostenoordeel ziet.

import type { RetourRedenWaarde } from "@/lib/api";

export const GRATIS_REDENEN: RetourRedenWaarde[] = [
  "verkeerd_product_geleverd",
  "pakket_beschadigd_bij_aankomst",
  "verpakking_beschadigd_bij_aankomst",
  "product_defect_kapot",
  "product_te_laat_geleverd",
];

// Schade-redenen: verplichte toelichting + foto-upload met de
// schade-instructies (verpakking gescheurd volgt bewust dezelfde
// route als pakket beschadigd, opdracht 21-07).
export const SCHADE_REDENEN: RetourRedenWaarde[] = [
  "pakket_beschadigd_bij_aankomst",
  "verpakking_beschadigd_bij_aankomst",
  "product_defect_kapot",
];

export const GEOPEND_GEVOELIGE_REDENEN: RetourRedenWaarde[] = [
  "allergische_reactie",
  "andere_reden",
];

// De vaste volgorde 1-10 van opdracht 21-07.
export const ALLE_REDENEN: RetourRedenWaarde[] = [
  "herroepingsrecht",
  "verkeerd_product_besteld",
  "verkeerd_product_geleverd",
  "pakket_beschadigd_bij_aankomst",
  "verpakking_beschadigd_bij_aankomst",
  "product_defect_kapot",
  "voldoet_niet_aan_verwachtingen",
  "product_te_laat_geleverd",
  "allergische_reactie",
  "andere_reden",
];

export interface ArtikelFormulier {
  index: number;
  aantal: number;
  reden: RetourRedenWaarde | "";
  verpakkingGeopend: boolean | null;
  toelichting: string;
  toelichtingKeuze: string;
  omschrijvingDuidelijk: boolean | null;
  bijlageIds: string[];
}

export type ArtikelUitkomst =
  | "gratis"
  | "betaald"
  | "beoordeling"
  | "uitgesloten";

export function artikelUitkomst(
  artikel: ArtikelFormulier,
): ArtikelUitkomst | null {
  if (!artikel.reden) return null;
  if (GRATIS_REDENEN.includes(artikel.reden)) return "gratis";
  if (artikel.reden === "andere_reden") {
    if (artikel.verpakkingGeopend === null) return null;
    return artikel.verpakkingGeopend ? "uitgesloten" : "beoordeling";
  }
  if (artikel.reden === "allergische_reactie") {
    if (artikel.verpakkingGeopend === null) return null;
    return artikel.verpakkingGeopend ? "uitgesloten" : "betaald";
  }
  return "betaald";
}

export type PakketOordeel =
  | "gratis"
  | "kosten_klant"
  | "beoordeling"
  | "niet_toegestaan";

export function pakketOordeel(
  artikelen: ArtikelFormulier[],
): PakketOordeel | null {
  const uitkomsten = artikelen.map(artikelUitkomst);
  if (uitkomsten.some((u) => u === null)) return null;
  const toegelaten = uitkomsten.filter((u) => u !== "uitgesloten");
  if (toegelaten.length === 0) return "niet_toegestaan";
  if (toegelaten.includes("beoordeling")) return "beoordeling";
  if (toegelaten.includes("gratis")) return "gratis";
  return "kosten_klant";
}

export function artikelCompleet(artikel: ArtikelFormulier): boolean {
  if (!artikel.reden) return false;
  if (
    GEOPEND_GEVOELIGE_REDENEN.includes(artikel.reden) &&
    artikel.verpakkingGeopend === null
  ) {
    return false;
  }
  if (
    (SCHADE_REDENEN.includes(artikel.reden) ||
      artikel.reden === "andere_reden") &&
    artikel.toelichting.trim() === ""
  ) {
    return false;
  }
  if (artikel.reden === "voldoet_niet_aan_verwachtingen") {
    if (!artikel.toelichtingKeuze) return false;
    if (artikel.omschrijvingDuidelijk === null) return false;
  }
  return true;
}

// Client-side spiegels van de servergegevens voor de teksten die al
// vóór de lookup in beeld staan (zekerhedenstrip en retourbeleid-blok,
// designronde 29-07). Zodra de lookup er is, geldt voor de kosten
// altijd het live bedrag uit retourkosten_cents.
// Spiegel van RETOUR_VERWERKING_WERKDAGEN (app/services/retour_mail.py).
export const VERWERKING_WERKDAGEN = 7;
// Spiegel van de default in RetourInstellingen.kosten_cents.
export const STANDAARD_RETOURKOSTEN_CENTS = 795;

export function formatteerBedrag(cents: number): string {
  return new Intl.NumberFormat("nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);
}
